import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminSupport() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/support/threads");
      setThreads(data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="admin-support">
      <div className="mb-6">
        <p className="overline mb-2">Admin · Support</p>
        <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Conversations</h1>
        <p className="mt-2 text-sm text-emerald-100/60">
          Threaded chat with signed-in customers. New messages arrive every few
          seconds.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
        <div className="card-edge max-h-[70vh] overflow-y-auto p-2">
          {loading && <p className="p-4 text-xs text-emerald-100/50">Loading…</p>}
          {!loading && threads.length === 0 && <p className="p-4 text-xs text-emerald-100/50">No conversations yet.</p>}
          <div className="grid gap-1">
            {threads.map((t) => (
              <button
                key={t.id}
                onClick={() => setActive(t)}
                data-testid={`admin-thread-${t.id}`}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  active?.id === t.id
                    ? "border-lime-400/40 bg-emerald-500/10"
                    : "border-transparent hover:border-emerald-500/20 hover:bg-emerald-500/[0.04]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="truncate text-sm font-medium text-white">{t.subject}</p>
                  {t.unread_admin > 0 && <span className="chip chip-lime !py-0.5 !text-[9px]">{t.unread_admin}</span>}
                </div>
                <p className="mt-1 truncate text-[11px] text-emerald-100/50">{t.user_email}</p>
                <p className="mt-0.5 font-mono-edge text-[10px] text-emerald-100/40">
                  {t.status} · {new Date(t.last_message_at).toLocaleString()}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div className="card-moss min-h-[70vh]">
          {!active ? (
            <div className="flex h-full items-center justify-center p-12">
              <p className="text-sm text-emerald-100/50">Select a conversation to reply.</p>
            </div>
          ) : (
            <ThreadPanel thread={active} onStatusChange={(s) => { setActive({ ...active, status: s }); load(); }} />
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadPanel({ thread, onStatusChange }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/support/threads/${thread.id}/messages`);
      setMsgs(data.messages);
    } catch { /* silent */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
  }, [thread.id]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/support/threads/${thread.id}/messages`, { body: text });
      setMsgs((m) => [...m, data]);
      setText("");
    } catch (e) {
      toast.error("Failed to send");
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async () => {
    const next = thread.status === "open" ? "resolved" : "open";
    try {
      await api.patch(`/admin/support/threads/${thread.id}/status`, { status: next });
      toast.success(`Marked ${next}`);
      onStatusChange(next);
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between border-b border-emerald-500/12 p-5">
        <div>
          <p className="overline">{thread.status}</p>
          <h3 className="mt-1 font-editorial text-xl font-medium text-white">{thread.subject}</h3>
          <p className="text-xs text-emerald-100/50">{thread.user_name} · {thread.user_email}</p>
        </div>
        <button onClick={toggleStatus} data-testid="admin-thread-toggle" className="btn-edge-ghost text-xs">
          {thread.status === "open" ? <><CheckCircle2 size={12} /> Resolve</> : <><RotateCcw size={12} /> Reopen</>}
        </button>
      </div>
      <div ref={boxRef} className="flex-1 space-y-2.5 overflow-y-auto bg-[#061510]/40 p-5">
        {msgs.map((m) => {
          const mine = m.sender_id === user?.id; // admin outgoing
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[70%] px-3.5 py-2 text-sm ${mine ? "bubble-user" : "bubble-admin"}`}>
                <p className="whitespace-pre-wrap">{m.body}</p>
                <p className={`mt-1 text-[10px] ${mine ? "text-[#04150c]/70" : "text-emerald-100/50"}`}>
                  {m.sender_name} · {new Date(m.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="flex items-center gap-2 border-t border-emerald-500/12 p-4">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Reply as admin…"
          data-testid="admin-msg-input"
          className="flex-1 rounded-full border border-emerald-500/15 bg-[#0d2416]/60 px-4 py-2.5 text-sm text-white focus:border-emerald-400/60 focus:outline-none"
        />
        <button
          disabled={busy}
          data-testid="admin-msg-send"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-b from-emerald-400 to-emerald-500 text-emerald-950 hover:-translate-y-0.5 disabled:opacity-50"
          aria-label="Send"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </div>
  );
}
